import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Download,
  FileText,
  ImageIcon,
  Layers,
  MapPin,
  Paperclip,
  Pencil,
  Play,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, apiBase } from "@/api/client";
import {
  useArchiveJob,
  useCreateBlankQuote,
  useCurrentUser,
  useDeleteJobAttachment,
  useGenerateQuoteFromText,
  useGenerateQuoteFromWalkthroughs,
  useJob,
  useJobAttachments,
  useQuotesForJob,
  useRenameJobAttachment,
  useUnarchiveJob,
  useUpdateJob,
  useUploadJobAttachment,
  useUploadWalkthrough,
  useWalkthroughsForJob,
} from "@/api/hooks";
import type { JobAttachmentResponse, JobDetail } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import {
  ACCEPT_ATTR,
  attachmentKind,
  filenameWithoutExtension,
  validateAttachment,
} from "@/lib/attachments";
import { formatBytes, formatCents, formatTime, shortId } from "@/lib/format";

export function JobDetailPage() {
  const { jobId = "" } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const jobQuery = useJob(jobId);
  const walkthroughsQuery = useWalkthroughsForJob(jobId);
  const quotesQuery = useQuotesForJob(jobId);
  const upload = useUploadWalkthrough(jobId);
  const blankQuote = useCreateBlankQuote();
  const generateCombined = useGenerateQuoteFromWalkthroughs();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [scopeFormOpen, setScopeFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "attachments">("overview");

  // Combine-mode state for the walkthroughs section. Off by default
  // (single-walkthrough is the 95% case); the contractor toggles it
  // on to multi-select two or more walkthroughs and generate ONE
  // combined quote with AI dedup of overlapping work.
  const [combineMode, setCombineMode] = useState(false);
  const [combineSelection, setCombineSelection] = useState<string[]>([]);
  const [combineError, setCombineError] = useState<string | null>(null);

  function toggleCombine(id: string) {
    setCombineSelection((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function generateCombinedQuote() {
    if (combineSelection.length < 1) return;
    setCombineError(null);
    try {
      const quote = await generateCombined.mutateAsync({
        walkthroughIds: combineSelection,
        jobId,
      });
      // Single-source generations land on the walkthrough detail
      // page; combined sources land on the quote directly since
      // there's no canonical source to return to.
      setCombineMode(false);
      setCombineSelection([]);
      navigate(`/quotes/${quote.id}`);
    } catch (err) {
      setCombineError(
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Couldn't generate combined quote.",
      );
    }
  }

  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    try {
      await upload.mutateAsync(file);
      if (fileInput.current) fileInput.current.value = "";
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed.");
    }
  }

  async function onCreateBlank() {
    try {
      const quote = await blankQuote.mutateAsync(jobId);
      navigate(`/quotes/${quote.id}`);
    } catch {
      /* surfaced via blankQuote.error */
    }
  }

  if (jobQuery.isLoading) {
    return <p className="text-sm text-graphite-soft">Loading job…</p>;
  }

  if (jobQuery.error) {
    return (
      <p className="text-sm text-red-pencil">
        {jobQuery.error instanceof ApiError ? jobQuery.error.detail : "Load failed."}
      </p>
    );
  }

  const job = jobQuery.data;
  if (!job) return null;

  return (
    <div className="space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-graphite-soft hover:text-graphite"
      >
        <ArrowLeft className="h-3 w-3" aria-hidden />
        All jobs
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 font-mono text-2xl font-bold tracking-tight">
            {job.title}
            <StatusBadge status={job.status} />
          </h1>
          <p className="mt-1 text-sm text-graphite-soft">
            {job.client_id && job.client_name && (
              <>
                Client:{" "}
                <Link
                  to={`/clients/${job.client_id}`}
                  className="font-medium text-graphite hover:text-ink hover:underline"
                >
                  {job.client_name}
                </Link>
                <span className="mx-2 text-graphite-faint">·</span>
              </>
            )}
            Created by <span className="font-medium text-graphite">{job.creator_display_name}</span>
            <span className="mx-2 text-graphite-faint">·</span>
            <span className="text-graphite-soft">{job.organization_name}</span>
            <span className="mx-2 text-graphite-faint">·</span>
            <span className="font-mono text-xs">{shortId(job.id)}</span>
          </p>
          {job.description && (
            <p className="mt-3 max-w-3xl text-sm text-graphite">{job.description}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft">
            <Upload className="h-3.5 w-3.5" aria-hidden />
            {upload.isPending ? "Uploading…" : "Upload walkthrough"}
            <input
              ref={fileInput}
              type="file"
              accept="video/*"
              className="sr-only"
              onChange={onFileChange}
              disabled={upload.isPending}
            />
          </label>
          <button
            type="button"
            onClick={() => setScopeFormOpen(true)}
            className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Generate from text
          </button>
          <button
            type="button"
            onClick={onCreateBlank}
            disabled={blankQuote.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {blankQuote.isPending ? "Creating…" : "Manual quote"}
          </button>
          <ProjectStatusButtons job={job} />
          <ArchiveToggleButton job={job} />
        </div>
      </header>

      {uploadError && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {uploadError}
        </p>
      )}

      {blankQuote.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {blankQuote.error instanceof ApiError
            ? blankQuote.error.detail
            : "Could not create quote."}
        </p>
      )}

      {scopeFormOpen && (
        <ScopeTextForm
          jobId={jobId}
          onClose={() => setScopeFormOpen(false)}
          onCreated={(quoteId) => navigate(`/quotes/${quoteId}`)}
        />
      )}

      <JobSiteScheduleCard jobId={jobId} job={job} />

      <nav className="flex border-b border-rule" aria-label="Job sections">
        {(
          [
            { key: "overview", label: "Overview" },
            { key: "attachments", label: "Attachments" },
          ] as const
        ).map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={[
                "-mb-px border-b-2 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-wider transition",
                active
                  ? "border-graphite text-graphite"
                  : "border-transparent text-graphite-soft hover:text-graphite",
              ].join(" ")}
              aria-current={active ? "page" : undefined}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === "attachments" ? (
        <JobAttachmentsTab jobId={jobId} />
      ) : (
        <>
          {/* Quotes section */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-mono text-lg font-bold tracking-tight">Quotes</h2>
              <span className="label-mono">{quotesQuery.data?.length ?? 0} total</span>
            </div>
            {quotesQuery.data && quotesQuery.data.length === 0 ? (
              <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
                No quotes yet. Use one of the buttons above — upload a video to record scope, type
                the scope yourself for AI to estimate, or start a fully manual quote.
              </p>
            ) : (
              <ul className="space-y-2">
                {quotesQuery.data?.map((q) => (
                  <li
                    key={q.id}
                    className="rounded-lg border border-rule bg-paper-warm transition hover:bg-pencil-yellow-soft"
                  >
                    <Link
                      to={`/quotes/${q.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-3.5 w-3.5 text-graphite-soft" aria-hidden />
                        <span className="font-mono text-sm">{shortId(q.id)}</span>
                        <span
                          className={[
                            "rounded px-2 py-0.5 font-mono text-xs",
                            q.status === "draft"
                              ? "bg-paper text-graphite-soft"
                              : q.status === "ready"
                                ? "bg-pencil-yellow-soft text-graphite"
                                : q.status === "sent"
                                  ? "bg-graphite text-paper"
                                  : q.status === "accepted"
                                    ? "bg-pencil-yellow text-graphite"
                                    : "bg-red-pencil/10 text-red-pencil",
                          ].join(" ")}
                        >
                          {q.status}
                        </span>
                        <span className="text-sm text-graphite-soft">
                          {q.line_items.length} line
                          {q.line_items.length === 1 ? "" : "s"}
                        </span>
                        <span className="font-mono text-[10px] text-graphite-faint">
                          {q.walkthrough_id ? "from video" : "manual / typed"}
                        </span>
                      </div>
                      <span className="font-mono text-sm font-semibold tabular-nums text-graphite">
                        {formatCents(q.total_cents)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Walkthroughs section */}
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <h2 className="font-mono text-lg font-bold tracking-tight">Walkthroughs</h2>
                <span className="label-mono">
                  {walkthroughsQuery.data?.length ?? 0} total
                </span>
              </div>
              {/* Combine toggle. Hidden when there are fewer than 2
                  processed walkthroughs (no point picking from a
                  list of 1). Resets selection on toggle so the
                  contractor doesn't carry over stale picks. */}
              {processedCount(walkthroughsQuery.data) >= 2 && (
                <button
                  type="button"
                  onClick={() => {
                    setCombineMode((m) => !m);
                    setCombineSelection([]);
                    setCombineError(null);
                  }}
                  className={
                    "inline-flex items-center gap-1.5 rounded border px-2.5 py-1 font-mono text-xs font-semibold transition " +
                    (combineMode
                      ? "border-graphite bg-graphite text-paper hover:bg-graphite-soft"
                      : "border-rule bg-paper-warm text-graphite-soft hover:bg-pencil-yellow-soft hover:text-graphite")
                  }
                >
                  <Layers className="h-3 w-3" aria-hidden />
                  {combineMode ? "Cancel combine" : "Combine into quote"}
                </button>
              )}
            </div>

            {combineMode && (
              <div className="rounded-lg border border-pencil-yellow bg-pencil-yellow-soft px-3 py-2 text-xs text-graphite">
                <p>
                  Pick two or more processed walkthroughs to merge into a
                  single quote. AI dedups overlapping work; the order you
                  pick becomes the source priority (first = primary).
                </p>
              </div>
            )}

            {walkthroughsQuery.data && walkthroughsQuery.data.length === 0 ? (
              <div className="rounded-lg border border-rule bg-paper-warm p-8 text-center">
                <p className="text-sm text-graphite-soft">
                  No walkthroughs yet. Hit "Upload walkthrough" to send a video for processing.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {walkthroughsQuery.data?.map((w) => {
                  const selectable =
                    combineMode && w.status === "processed";
                  const selectedIdx = combineSelection.indexOf(w.id);
                  const isSelected = selectedIdx >= 0;
                  return (
                    <li
                      key={w.id}
                      className={
                        "rounded-lg border transition " +
                        (isSelected
                          ? "border-graphite bg-pencil-yellow-soft"
                          : "border-rule bg-paper-warm hover:bg-pencil-yellow-soft")
                      }
                    >
                      {selectable ? (
                        <button
                          type="button"
                          onClick={() => toggleCombine(w.id)}
                          className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left"
                        >
                          <span className="flex min-w-0 flex-1 items-start gap-3">
                            <span
                              className={
                                "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border font-mono text-xs " +
                                (isSelected
                                  ? "border-graphite bg-graphite text-paper"
                                  : "border-rule bg-paper text-transparent")
                              }
                              aria-hidden
                            >
                              {isSelected ? selectedIdx + 1 : ""}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2">
                                <span className="truncate font-medium text-graphite">
                                  {w.filename}
                                </span>
                                <StatusBadge status={w.status} />
                              </span>
                              <span className="mt-1 flex flex-wrap gap-x-3 font-mono text-xs text-graphite-soft tabular-nums">
                                <span>{formatBytes(w.size_bytes)}</span>
                                <span>{w.content_type}</span>
                                <span>
                                  {formatTime(w.uploaded_at ?? w.created_at)}
                                </span>
                              </span>
                            </span>
                          </span>
                        </button>
                      ) : (
                        <Link
                          to={`/walkthroughs/${w.id}`}
                          className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                        >
                          <div className="min-w-0 flex-1">
                            {combineMode && w.status !== "processed" && (
                              <span className="mb-1 inline-block font-mono text-[10px] uppercase tracking-wider text-graphite-faint">
                                Not selectable — must be processed
                              </span>
                            )}
                            <div className="flex items-center gap-2">
                              <span className="truncate font-medium text-graphite">
                                {w.filename}
                              </span>
                              <StatusBadge status={w.status} />
                            </div>
                            <div className="mt-1 flex flex-wrap gap-x-3 font-mono text-xs text-graphite-soft tabular-nums">
                              <span>{formatBytes(w.size_bytes)}</span>
                              <span>{w.content_type}</span>
                              <span>{formatTime(w.uploaded_at ?? w.created_at)}</span>
                              <span className="text-graphite-faint">{shortId(w.id)}</span>
                            </div>
                            {w.error_message && (
                              <p className="mt-1 text-xs text-red-pencil">
                                {w.error_message}
                              </p>
                            )}
                          </div>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {combineMode && (
              <div className="rounded-lg border border-rule bg-paper-warm p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-mono text-xs text-graphite-soft">
                    {combineSelection.length === 0
                      ? "Pick at least one walkthrough above."
                      : combineSelection.length === 1
                        ? "1 walkthrough selected — pick another to combine, or generate a single-source quote."
                        : `${combineSelection.length} walkthroughs selected · numbered in pick order`}
                  </p>
                  <button
                    type="button"
                    onClick={generateCombinedQuote}
                    disabled={
                      combineSelection.length === 0 ||
                      generateCombined.isPending
                    }
                    className="inline-flex items-center gap-1.5 rounded border border-graphite bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles className="h-3 w-3" aria-hidden />
                    {generateCombined.isPending
                      ? "Generating…"
                      : `Generate combined quote${
                          combineSelection.length > 0
                            ? ` (${combineSelection.length})`
                            : ""
                        }`}
                  </button>
                </div>
                {combineError && (
                  <p className="mt-2 font-mono text-xs text-red-pencil">
                    {combineError}
                  </p>
                )}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** Count walkthroughs in ``processed`` status — only those are
 *  legitimate sources for a combined quote. We hide the "Combine
 *  into quote" toggle until the contractor has at least two so
 *  the affordance doesn't appear pointless. */
function processedCount(
  ws: { status: string }[] | undefined,
): number {
  if (!ws) return 0;
  return ws.filter((w) => w.status === "processed").length;
}

/** Project execution buttons — "Start work" / "Mark complete"
 *  on jobs that are post-acceptance. Only renders on
 *  ``won`` (offers Start work) or ``in_progress`` (offers
 *  Mark complete). Other statuses get no button.
 *
 *  Server auto-stamps ``started_at`` / ``completed_at`` on the
 *  matching transition; we just refetch the job to surface
 *  the new state. */
function ProjectStatusButtons({ job }: { job: JobDetail }) {
  const update = useUpdateJob(job.id);

  const variant: { label: string; next: "in_progress" | "complete"; icon: typeof Play } | null =
    job.status === "won"
      ? { label: "Start work", next: "in_progress", icon: Play }
      : job.status === "in_progress"
        ? { label: "Mark complete", next: "complete", icon: CheckCircle2 }
        : null;

  if (!variant) return null;

  return (
    <button
      type="button"
      onClick={() => update.mutate({ status: variant.next })}
      disabled={update.isPending}
      className="inline-flex items-center gap-1.5 rounded border border-graphite bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-50"
    >
      <variant.icon className="h-3.5 w-3.5" aria-hidden />
      {update.isPending ? "Saving…" : variant.label}
    </button>
  );
}

/** Archive / unarchive button. The label + icon flip based on
 *  current status; pressed-state shows a tiny confirmation chip
 *  so the contractor sees the action landed without a full reload. */
function ArchiveToggleButton({
  job,
}: {
  job: { id: string; status: string };
}) {
  const archive = useArchiveJob();
  const unarchive = useUnarchiveJob();
  const isArchived = job.status === "archived";
  const pending = archive.isPending || unarchive.isPending;

  return (
    <button
      type="button"
      onClick={() => {
        if (isArchived) {
          unarchive.mutate(job.id);
        } else {
          archive.mutate(job.id);
        }
      }}
      disabled={pending}
      title={
        isArchived
          ? "Restore this job to the active list"
          : "Hide this job from the default Jobs list"
      }
      className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite-soft transition hover:bg-pencil-yellow-soft hover:text-graphite disabled:opacity-50"
    >
      {isArchived ? (
        <>
          <ArchiveRestore className="h-3.5 w-3.5" aria-hidden />
          {pending ? "Restoring…" : "Unarchive"}
        </>
      ) : (
        <>
          <Archive className="h-3.5 w-3.5" aria-hidden />
          {pending ? "Archiving…" : "Archive"}
        </>
      )}
    </button>
  );
}

/** Inline form to generate a quote from typed scope (no video). */
function ScopeTextForm({
  jobId,
  onClose,
  onCreated,
}: {
  jobId: string;
  onClose: () => void;
  onCreated: (quoteId: string) => void;
}) {
  const generate = useGenerateQuoteFromText();
  const [scopeText, setScopeText] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!scopeText.trim()) return;
    try {
      const quote = await generate.mutateAsync({
        jobId,
        scopeText: scopeText.trim(),
      });
      onCreated(quote.id);
    } catch {
      /* surfaced below */
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider">
          Generate from text
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-graphite-soft">
        Describe the job — rooms, work, materials, dimensions if you have them. The AI will turn it
        into line items grounded in your pricing rules.
      </p>

      <textarea
        value={scopeText}
        onChange={(e) => setScopeText(e.target.value)}
        rows={8}
        placeholder={
          "Kitchen remodel — ~140 sqft. Demo existing cabinets and laminate counters. Install new shaker cabinets (12 lf upper, 14 lf lower), quartz counters, undermount sink, new disposal. Refinish hardwood floor. Paint walls + ceiling. Client wants mid-range fixtures, 6-week timeline."
        }
        required
        className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
      />

      {generate.error && (
        <p className="text-sm text-red-pencil">
          {generate.error instanceof ApiError ? generate.error.detail : "Could not generate quote."}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={generate.isPending || !scopeText.trim()}
          className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          {generate.isPending ? "Generating…" : "Generate quote"}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------
// JobSiteScheduleCard — site address + scheduled-for + notes,
// inline-editable. Read mode collapses to a compact row when both
// fields are empty so an unscheduled, address-inherited job
// doesn't take up screen space.
// ---------------------------------------------------------------

function JobSiteScheduleCard({
  jobId,
  job,
}: {
  jobId: string;
  job: JobDetail;
}) {
  const update = useUpdateJob(jobId);
  const [editing, setEditing] = useState(false);
  const [siteAddress, setSiteAddress] = useState(job.site_address ?? "");
  const [scheduledForLocal, setScheduledForLocal] = useState(isoToLocalInput(job.scheduled_for));
  const [scheduleNotes, setScheduleNotes] = useState(job.schedule_notes ?? "");
  const [error, setError] = useState<string | null>(null);

  // Re-sync when job re-fetches (e.g. after save).
  useEffect(() => {
    setSiteAddress(job.site_address ?? "");
    setScheduledForLocal(isoToLocalInput(job.scheduled_for));
    setScheduleNotes(job.schedule_notes ?? "");
  }, [job.site_address, job.scheduled_for, job.schedule_notes]);

  const hasAnyValue = !!job.site_address || !!job.scheduled_for || !!job.schedule_notes;

  function handleSave() {
    setError(null);
    let scheduledIso: string | null = null;
    if (scheduledForLocal.trim()) {
      const parsed = new Date(scheduledForLocal);
      if (Number.isNaN(parsed.getTime())) {
        setError("Schedule date isn't valid.");
        return;
      }
      scheduledIso = parsed.toISOString();
    }
    update.mutate(
      {
        site_address: siteAddress.trim() ? siteAddress.trim() : null,
        scheduled_for: scheduledIso,
        schedule_notes: scheduleNotes.trim() ? scheduleNotes.trim() : null,
      },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      },
    );
  }

  if (!editing) {
    return (
      <section className="rounded-lg border border-rule bg-paper-warm p-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Site + schedule
          </h2>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 font-mono text-xs text-graphite-soft hover:text-graphite"
          >
            <Pencil className="h-3 w-3" aria-hidden />
            {hasAnyValue ? "Edit" : "Add"}
          </button>
        </div>
        {hasAnyValue ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {job.site_address ? (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-graphite-soft" aria-hidden />
                <span className="whitespace-pre-line text-graphite">{job.site_address}</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm text-graphite-soft">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-graphite-faint" aria-hidden />
                <span>Address inherited from client</span>
              </div>
            )}
            {job.scheduled_for ? (
              <div className="flex items-start gap-2 text-sm">
                <CalendarClock
                  className="mt-0.5 h-4 w-4 flex-none text-graphite-soft"
                  aria-hidden
                />
                <div>
                  <div className="text-graphite">
                    {new Date(job.scheduled_for).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </div>
                  {job.schedule_notes && (
                    <div className="mt-0.5 text-xs text-graphite-soft">{job.schedule_notes}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm text-graphite-soft">
                <CalendarClock
                  className="mt-0.5 h-4 w-4 flex-none text-graphite-faint"
                  aria-hidden
                />
                <span>Unscheduled</span>
              </div>
            )}
          </div>
        ) : (
          <p className="mt-2 text-sm text-graphite-soft">No site address or scheduled date yet.</p>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
          Site + schedule
        </h2>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 space-y-3">
        <div>
          <label
            htmlFor="job-site-address"
            className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-graphite-soft"
          >
            Site address
          </label>
          <textarea
            id="job-site-address"
            value={siteAddress}
            onChange={(e) => setSiteAddress(e.target.value)}
            rows={3}
            placeholder={"123 Main St\nCharlotte, NC 28202"}
            className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
          />
          <p className="mt-1 font-mono text-[11px] text-graphite-faint">
            Leave blank to inherit from the linked client.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="job-scheduled-for"
              className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Scheduled for
            </label>
            <input
              id="job-scheduled-for"
              type="datetime-local"
              value={scheduledForLocal}
              onChange={(e) => setScheduledForLocal(e.target.value)}
              className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
            />
            <p className="mt-1 font-mono text-[11px] text-graphite-faint">Empty = unscheduled.</p>
          </div>
          <div>
            <label
              htmlFor="job-schedule-notes"
              className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Note
            </label>
            <input
              id="job-schedule-notes"
              type="text"
              value={scheduleNotes}
              onChange={(e) => setScheduleNotes(e.target.value)}
              maxLength={500}
              placeholder="AM only · client at 9 · &c."
              className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
            />
          </div>
        </div>
        {error && <p className="text-xs text-red-pencil">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={update.isPending}
            className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </section>
  );
}

/** Convert an ISO string from the server into the local
 *  ``datetime-local`` value expected by HTML5. Returns "" for
 *  null/undefined. The input element renders in the user's
 *  browser time zone, so we slice the local-rendered ISO. */
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// ---------------------------------------------------------------
// JobAttachmentsTab — curated, contractor-managed attachments on
// the job. Combines direct uploads (paperclip / drag-drop) with
// promoted-from-comment attachments. Rename + delete are
// org_admin-gated; the buttons hide for org_member.
// ---------------------------------------------------------------

function JobAttachmentsTab({ jobId }: { jobId: string }) {
  const me = useCurrentUser();
  const isOrgAdmin = me.data?.role === "org_admin" || me.data?.role === "pencilout_admin";
  const list = useJobAttachments(jobId);
  const upload = useUploadJobAttachment(jobId);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<JobAttachmentResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JobAttachmentResponse | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const labelInputRef = useRef<HTMLInputElement>(null);

  // Land focus on the label field once the staged-file form replaces
  // the drop zone, so keyboard users don't have to tab in.
  useEffect(() => {
    if (pendingFile) labelInputRef.current?.focus();
  }, [pendingFile]);

  function selectFile(file: File | null) {
    setError(null);
    if (!file) {
      setPendingFile(null);
      setLabel("");
      return;
    }
    const reason = validateAttachment(file);
    if (reason) {
      setError(reason);
      setPendingFile(null);
      return;
    }
    setPendingFile(file);
    setLabel(filenameWithoutExtension(file.name));
  }

  function reset() {
    setPendingFile(null);
    setLabel("");
    setError(null);
    upload.reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function submit() {
    if (!pendingFile) return;
    const trimmed = label.trim();
    if (!trimmed) {
      setError("Label is required.");
      return;
    }
    setError(null);
    upload.mutate(
      { file: pendingFile, label: trimmed },
      {
        onSuccess: reset,
        onError: (err) => {
          setError(err instanceof ApiError ? err.detail : "Upload failed.");
        },
      },
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-lg font-bold tracking-tight">Attachments</h2>
        <span className="label-mono">{list.data?.length ?? 0} total</span>
      </div>

      {/* Upload zone — drag-drop OR pick. Once a file is staged, an
          inline label input + Save button replaces the drop zone so
          the user supplies a label before the POST. */}
      {!pendingFile ? (
        <div
          className={[
            "rounded-lg border-2 border-dashed p-6 text-center transition",
            dragOver ? "border-graphite bg-pencil-yellow-soft" : "border-rule bg-paper-warm",
          ].join(" ")}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const dropped = Array.from(e.dataTransfer.files ?? [])[0] ?? null;
            selectFile(dropped);
          }}
        >
          <Paperclip className="mx-auto h-5 w-5 text-graphite-soft" aria-hidden />
          <p className="mt-2 text-sm text-graphite-soft">Drag a file here, or</p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              selectFile(file);
              if (e.target) e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-2 inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
          >
            <Upload className="h-3.5 w-3.5" aria-hidden />
            Choose file
          </button>
          <p className="mt-2 font-mono text-[10px] text-graphite-faint">
            JPG, PNG, HEIC, WEBP up to 10 MB. PDF up to 20 MB.
          </p>
          {error && <p className="mt-2 text-sm text-red-pencil">{error}</p>}
        </div>
      ) : (
        <div className="space-y-3 rounded-lg border border-rule bg-paper-warm p-4">
          <div className="flex items-center gap-2">
            {attachmentKind(pendingFile.type) === "image" ? (
              <ImageIcon className="h-4 w-4 text-graphite-soft" aria-hidden />
            ) : (
              <FileText className="h-4 w-4 text-graphite-soft" aria-hidden />
            )}
            <span className="min-w-0 flex-1 truncate font-medium text-graphite">
              {pendingFile.name}
            </span>
            <span className="font-mono text-xs tabular-nums text-graphite-soft">
              {formatBytes(pendingFile.size)}
            </span>
            <button
              type="button"
              onClick={reset}
              className="text-graphite-soft hover:text-graphite"
              aria-label="Cancel"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <div>
            <label
              htmlFor="job-attachment-label"
              className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Label (required)
            </label>
            <input
              ref={labelInputRef}
              id="job-attachment-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={255}
              placeholder="What this is — signed scope, kitchen demo, etc."
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
            />
          </div>
          {error && <p className="text-sm text-red-pencil">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={reset}
              disabled={upload.isPending}
              className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={upload.isPending || !label.trim()}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              <Upload className="h-3.5 w-3.5" aria-hidden />
              {upload.isPending ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      )}

      {list.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {list.error instanceof ApiError ? list.error.detail : "Could not load attachments."}
        </p>
      )}

      {list.isLoading ? (
        <p className="text-sm text-graphite-soft">Loading attachments…</p>
      ) : list.data && list.data.length === 0 ? (
        <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
          No attachments yet. Drop a file above, or send one over from a quote conversation.
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data?.map((att) => (
            <li key={att.id}>
              <JobAttachmentCard
                attachment={att}
                canManage={isOrgAdmin}
                onRename={() => setRenameTarget(att)}
                onDelete={() => setDeleteTarget(att)}
              />
            </li>
          ))}
        </ul>
      )}

      {renameTarget && (
        <RenameJobAttachmentDialog
          jobId={jobId}
          attachment={renameTarget}
          onClose={() => setRenameTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteJobAttachmentConfirm
          jobId={jobId}
          attachment={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </section>
  );
}

/** One attachment in the grid. Image MIMEs render as a clickable
 *  thumbnail; PDF + other MIMEs render an icon. Hover surfaces the
 *  Rename + Delete affordances when ``canManage`` is true. */
function JobAttachmentCard({
  attachment,
  canManage,
  onRename,
  onDelete,
}: {
  attachment: JobAttachmentResponse;
  canManage: boolean;
  onRename: () => void;
  onDelete: () => void;
}) {
  const kind = attachmentKind(attachment.mime_type);
  const url = `${apiBase}${attachment.download_url}`;
  return (
    <article className="group relative overflow-hidden rounded-lg border border-rule bg-paper-warm">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="block bg-paper"
        aria-label={`Download ${attachment.label}`}
      >
        {kind === "image" ? (
          <img
            src={url}
            alt={attachment.label}
            loading="lazy"
            className="h-40 w-full object-cover"
          />
        ) : (
          <div className="flex h-40 w-full items-center justify-center bg-paper">
            <FileText className="h-10 w-10 text-graphite-soft" aria-hidden />
          </div>
        )}
      </a>
      <div className="space-y-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3
            className="min-w-0 flex-1 truncate font-medium text-graphite"
            title={attachment.label}
          >
            {attachment.label}
          </h3>
          {attachment.source === "promoted_from_comment" && (
            <span
              className="flex-none rounded bg-pencil-yellow-soft px-1.5 py-0.5 font-mono text-[10px] text-graphite"
              title="Copied here from a quote conversation"
            >
              From chat
            </span>
          )}
        </div>
        <p
          className="truncate font-mono text-[10px] text-graphite-soft"
          title={attachment.original_filename}
        >
          {attachment.original_filename}
        </p>
        <div className="flex flex-wrap gap-x-2 font-mono text-[10px] tabular-nums text-graphite-soft">
          <span>{formatBytes(attachment.size_bytes)}</span>
          <span>{formatTime(attachment.created_at)}</span>
        </div>
        <div className="flex items-center justify-between gap-1.5 pt-1">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-mono text-[10px] text-graphite-soft transition hover:text-graphite"
          >
            <Download className="h-3 w-3" aria-hidden />
            Download
          </a>
          {canManage && (
            <div className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
              <button
                type="button"
                onClick={onRename}
                className="inline-flex items-center gap-1 rounded border border-rule bg-paper px-2 py-1 font-mono text-[10px] text-graphite-soft transition hover:bg-pencil-yellow-soft hover:text-graphite"
                aria-label={`Rename ${attachment.label}`}
              >
                <Pencil className="h-3 w-3" aria-hidden />
                Rename
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="inline-flex items-center gap-1 rounded border border-rule bg-paper px-2 py-1 font-mono text-[10px] text-graphite-soft transition hover:border-red-pencil hover:bg-red-pencil/5 hover:text-red-pencil"
                aria-label={`Delete ${attachment.label}`}
              >
                <Trash2 className="h-3 w-3" aria-hidden />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function RenameJobAttachmentDialog({
  jobId,
  attachment,
  onClose,
}: {
  jobId: string;
  attachment: JobAttachmentResponse;
  onClose: () => void;
}) {
  const rename = useRenameJobAttachment(jobId);
  const [label, setLabel] = useState(attachment.label);
  const labelInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Land focus in the label field on open so keyboard users
    // don't have to tab in.
    labelInputRef.current?.focus();
    labelInputRef.current?.select();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !rename.isPending) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rename.isPending, onClose]);

  const trimmed = label.trim();
  const canSave = trimmed.length > 0 && trimmed !== attachment.label && !rename.isPending;

  return (
    // biome-ignore lint/a11y/useSemanticElements: matches the ConfirmDialog modal pattern.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-attachment-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-graphite/40 px-6 py-12"
      onClick={(e) => {
        if (e.target === e.currentTarget && !rename.isPending) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !rename.isPending) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-rule bg-paper-warm shadow-lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSave) return;
            rename.mutate({ attachmentId: attachment.id, label: trimmed }, { onSuccess: onClose });
          }}
        >
          <div className="flex items-start gap-3 px-5 py-4">
            <div className="flex-1">
              <h2
                id="rename-attachment-title"
                className="font-mono text-base font-bold tracking-tight text-graphite"
              >
                Rename attachment
              </h2>
              <label
                htmlFor="rename-attachment-label"
                className="mt-3 block font-mono text-[11px] font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Label
              </label>
              <input
                ref={labelInputRef}
                id="rename-attachment-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={255}
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
              />
              {rename.error && (
                <p className="mt-2 text-sm text-red-pencil">
                  {rename.error instanceof ApiError ? rename.error.detail : "Rename failed."}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={rename.isPending}
              aria-label="Cancel"
              className="text-graphite-soft hover:text-graphite disabled:opacity-50"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <div className="flex justify-end gap-2 border-t border-rule bg-paper px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={rename.isPending}
              className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              {rename.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteJobAttachmentConfirm({
  jobId,
  attachment,
  onClose,
}: {
  jobId: string;
  attachment: JobAttachmentResponse;
  onClose: () => void;
}) {
  const del = useDeleteJobAttachment(jobId);
  return (
    <ConfirmDialog
      title="Delete this attachment?"
      variant="danger"
      confirmLabel={del.isPending ? "Deleting…" : "Delete"}
      busy={del.isPending}
      body={
        <>
          <p>
            Removes <span className="font-medium text-graphite">{attachment.label}</span> from this
            job. This can't be undone.
          </p>
          {del.error && (
            <p className="mt-2 text-red-pencil">
              {del.error instanceof ApiError ? del.error.detail : "Delete failed."}
            </p>
          )}
        </>
      }
      onCancel={() => {
        del.reset();
        onClose();
      }}
      onConfirm={() => {
        del.mutate(attachment.id, { onSuccess: onClose });
      }}
    />
  );
}
